import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuth } from '@/contexts/AuthContext'
import { usePermissions } from '@/hooks/usePermissions'
import {
  getProjectMembers,
  getProjectInvitations,
  updateProjectMember,
  removeProjectMember,
  revokeProjectInvitation,
  isProjectAdmin,
} from '@/lib/api/projectMembers'
import { getCurrentProfile } from '@/lib/api/profiles'
import type {
  ProjectMemberWithProfile,
  ProjectInvitation,
  ProjectRole,
} from '@/types/project-teams'
import type { Profile } from '@/types/project-teams'
import { RoleLabels } from '@/types/project-teams'
import { toast } from 'sonner'
import { Loader2, UserPlus, Trash2, Mail, Copy, User, Edit, Shield, ShieldCheck } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { InviteMemberDialog } from './InviteMemberDialog'
import { ProfileEditDialog } from '@/components/profile/ProfileEditDialog'
import { otpSchema, type OTPInput } from '@/lib/validations/auth'
import { useAnalytics } from '@/hooks/useAnalytics'

interface ProjectTeamTabProps {
  projectId: string
  projectOwnerId: string
}

export function ProjectTeamTab({ projectId, projectOwnerId }: ProjectTeamTabProps) {
  const { user, enrollMFA, verifyMFAEnrollment, unenrollMFA, checkMFAStatus } = useAuth()
  const permissions = usePermissions(projectId, projectOwnerId)
  const [members, setMembers] = useState<ProjectMemberWithProfile[]>([])
  const [invitations, setInvitations] = useState<ProjectInvitation[]>([])
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [profileDialogOpen, setProfileDialogOpen] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const { track } = useAnalytics()

  // 2FA state
  const [mfaEnabled, setMfaEnabled] = useState(false)
  const [enrolling, setEnrolling] = useState(false)
  const [qrCode, setQrCode] = useState<string | null>(null)
  const [secret, setSecret] = useState<string | null>(null)
  const [factorId, setFactorId] = useState<string | null>(null)
  const [verifying, setVerifying] = useState(false)
  const [unenrolling, setUnenrolling] = useState(false)

  const {
    register: registerOTP,
    handleSubmit: handleSubmitOTP,
    formState: { errors: otpErrors },
    reset: resetOTP,
  } = useForm<OTPInput>({
    resolver: zodResolver(otpSchema),
  })

  const isOwner = user?.id === projectOwnerId

  useEffect(() => {
    loadData()
    checkAdminStatus()
    loadProfile()
    loadMFAStatus()
  }, [projectId])

  const loadData = async () => {
    try {
      setLoading(true)
      const [membersData, invitationsData] = await Promise.all([
        getProjectMembers(projectId),
        getProjectInvitations(projectId).catch(() => []), // Non-admins can't see invitations
      ])

      setMembers(membersData)
      setInvitations(invitationsData)
    } catch (error) {
      console.error('Error loading team data:', error)
      toast.error('Failed to load team data')
    } finally {
      setLoading(false)
    }
  }

  const checkAdminStatus = async () => {
    const adminStatus = await isProjectAdmin(projectId)
    setIsAdmin(adminStatus)
  }

  const loadProfile = async () => {
    try {
      const data = await getCurrentProfile()
      setProfile(data)
    } catch (error) {
      console.error('Error loading profile:', error)
    }
  }

  const loadMFAStatus = async () => {
    try {
      const enabled = await checkMFAStatus()
      setMfaEnabled(enabled)
    } catch (error) {
      console.error('Error checking MFA status:', error)
    }
  }

  const handleEnrollMFA = async () => {
    try {
      setEnrolling(true)
      const { qr_code, secret, factorId } = await enrollMFA()
      setQrCode(qr_code)
      setSecret(secret)
      setFactorId(factorId)
      toast.success('Scan the QR code with your authenticator app')
    } catch (error) {
      console.error('Error enrolling MFA:', error)
      toast.error('Failed to set up 2FA')
    } finally {
      setEnrolling(false)
    }
  }

  const handleVerifyEnrollment = async (data: OTPInput) => {
    if (!factorId) {
      toast.error('Factor ID not found. Please try again.')
      return
    }

    try {
      setVerifying(true)
      await verifyMFAEnrollment(factorId, data.code)
      setMfaEnabled(true)
      setQrCode(null)
      setSecret(null)
      setFactorId(null)
      resetOTP()

      // Track 2FA enabled
      track('mfa_enabled', {
        project_id: projectId,
      })

      toast.success('2FA enabled successfully!')
    } catch (error) {
      console.error('Error verifying MFA:', error)
      toast.error('Invalid code. Please try again.')
    } finally {
      setVerifying(false)
    }
  }

  const handleUnenrollMFA = async () => {
    if (!confirm('Are you sure you want to disable 2FA? This will make your account less secure.')) {
      return
    }

    try {
      setUnenrolling(true)
      await unenrollMFA()
      setMfaEnabled(false)
      toast.success('2FA disabled')
    } catch (error) {
      console.error('Error unenrolling MFA:', error)
      toast.error('Failed to disable 2FA')
    } finally {
      setUnenrolling(false)
    }
  }

  const handleCancelEnrollment = () => {
    setQrCode(null)
    setSecret(null)
    setFactorId(null)
    resetOTP()
  }

  const handleUpdateRole = async (userId: string, newRole: ProjectRole) => {
    try {
      await updateProjectMember(projectId, userId, { role: newRole })
      toast.success('Member role updated')
      await loadData()
    } catch (error) {
      console.error('Error updating member role:', error)
      toast.error('Failed to update member role')
    }
  }

  const handleRemoveMember = async (userId: string, memberEmail: string) => {
    if (!confirm(`Are you sure you want to remove ${memberEmail} from this project?`)) {
      return
    }

    try {
      await removeProjectMember(projectId, userId)
      toast.success('Member removed from project')
      await loadData()
    } catch (error) {
      console.error('Error removing member:', error)
      toast.error('Failed to remove member')
    }
  }

  const handleRevokeInvitation = async (invitationId: string) => {
    try {
      await revokeProjectInvitation(invitationId)
      toast.success('Invitation revoked')
      await loadData()
    } catch (error) {
      console.error('Error revoking invitation:', error)
      toast.error('Failed to revoke invitation')
    }
  }

  const copyInviteLink = (token: string) => {
    const link = `${window.location.origin}/invitations/${token}`
    navigator.clipboard.writeText(link)
    toast.success('Invitation link copied to clipboard')
  }

  const canManageTeam = isOwner || isAdmin

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Your Profile Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Your Profile</CardTitle>
              <CardDescription>
                How you appear to other team members
              </CardDescription>
            </div>
            <Button onClick={() => setProfileDialogOpen(true)} variant="outline">
              <Edit className="mr-2 h-4 w-4" />
              Edit Profile
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-4">
            <div className="h-16 w-16 rounded-full overflow-hidden bg-muted flex items-center justify-center flex-shrink-0">
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt="Your avatar"
                  className="h-full w-full object-cover"
                />
              ) : (
                <User className="h-8 w-8 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold">
                  {profile?.full_name || 'No name set'}
                </h3>
                {isOwner && (
                  <Badge variant="outline">Project Owner</Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                {profile?.email || user?.email}
              </p>
              {profile?.bio && (
                <p className="text-sm">{profile.bio}</p>
              )}
              {!profile?.bio && (
                <p className="text-sm text-muted-foreground italic">
                  No bio yet. Add one to help teammates get to know you!
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Two-Factor Authentication Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {mfaEnabled ? (
              <ShieldCheck className="h-5 w-5 text-green-600" />
            ) : (
              <Shield className="h-5 w-5" />
            )}
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Add an extra layer of security to your account
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {mfaEnabled ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm">
                <div className="h-2 w-2 rounded-full bg-green-600"></div>
                <span className="text-green-600 font-medium">2FA is enabled</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Your account is protected with two-factor authentication. You'll need to enter a code from your authenticator app when signing in.
              </p>
              <Button
                onClick={handleUnenrollMFA}
                disabled={unenrolling}
                variant="destructive"
              >
                {unenrolling && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Disable 2FA
              </Button>
            </div>
          ) : qrCode ? (
            <div className="space-y-4">
              <div className="bg-muted p-4 rounded-md">
                <p className="text-sm font-medium mb-3">Step 1: Scan QR Code</p>
                <div className="bg-white p-4 rounded-md inline-block">
                  <img src={qrCode} alt="QR Code" className="w-48 h-48" />
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                </p>
              </div>

              <div className="bg-muted p-4 rounded-md">
                <p className="text-sm font-medium mb-2">Manual Entry</p>
                <p className="text-xs text-muted-foreground mb-2">
                  If you can't scan the QR code, enter this secret key manually:
                </p>
                <code className="block bg-background px-3 py-2 rounded text-xs font-mono break-all">
                  {secret}
                </code>
              </div>

              <form onSubmit={handleSubmitOTP(handleVerifyEnrollment)} className="space-y-4">
                <div>
                  <Label htmlFor="code">Step 2: Enter 6-digit code</Label>
                  <Input
                    id="code"
                    type="text"
                    placeholder="000000"
                    maxLength={6}
                    {...registerOTP('code')}
                    disabled={verifying}
                    className="font-mono text-center text-lg tracking-widest"
                  />
                  {otpErrors.code && (
                    <p className="text-sm text-destructive mt-1">{otpErrors.code.message}</p>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button type="submit" disabled={verifying}>
                    {verifying && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Verify and Enable
                  </Button>
                  <Button
                    type="button"
                    onClick={handleCancelEnrollment}
                    variant="outline"
                    disabled={verifying}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Two-factor authentication adds an extra layer of security by requiring a 6-digit code from your authenticator app when you sign in.
              </p>
              <Button
                onClick={handleEnrollMFA}
                disabled={enrolling}
              >
                {enrolling && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Enable 2FA
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Team Members Section - Hidden from contributors */}
      {permissions.canViewTeam && (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Team Members</CardTitle>
              <CardDescription>
                Manage who has access to this project
              </CardDescription>
            </div>
            {canManageTeam && (
              <Button onClick={() => setInviteDialogOpen(true)}>
                <UserPlus className="mr-2 h-4 w-4" />
                Invite Member
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
                {canManageTeam && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Project Owner Row */}
              {(() => {
                const ownerMember = members.find(m => m.user_id === projectOwnerId)
                return (
                  <TableRow>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {ownerMember?.profile?.full_name || ownerMember?.profile?.email || (projectOwnerId === user?.id ? 'You' : 'Project Owner')}
                          {projectOwnerId === user?.id && (
                            <Badge variant="outline" className="ml-2">
                              You
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {ownerMember?.profile?.email}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant="default">Owner</Badge>
                        <Badge variant="secondary">Admin</Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      {ownerMember?.joined_at
                        ? formatDistanceToNow(new Date(ownerMember.joined_at), {
                            addSuffix: true,
                          })
                        : '—'}
                    </TableCell>
                    {canManageTeam && <TableCell></TableCell>}
                  </TableRow>
                )
              })()}

              {/* Team Members (excluding owner since they're shown above) */}
              {members.filter(member => member.user_id !== projectOwnerId).map((member) => (
                <TableRow key={member.user_id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">
                        {member.profile?.full_name || member.profile?.email}
                        {member.user_id === user?.id && (
                          <Badge variant="outline" className="ml-2">
                            You
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {member.profile?.email}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {canManageTeam && member.user_id !== user?.id ? (
                      <Select
                        value={member.role}
                        onValueChange={(value) =>
                          handleUpdateRole(member.user_id, value as ProjectRole)
                        }
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">{RoleLabels.admin}</SelectItem>
                          <SelectItem value="editor">{RoleLabels.editor}</SelectItem>
                          <SelectItem value="viewer">{RoleLabels.viewer}</SelectItem>
                          <SelectItem value="contributor">{RoleLabels.contributor}</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge>{RoleLabels[member.role]}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {member.joined_at
                      ? formatDistanceToNow(new Date(member.joined_at), {
                          addSuffix: true,
                        })
                      : 'Pending'}
                  </TableCell>
                  {canManageTeam && (
                    <TableCell className="text-right">
                      {member.user_id !== user?.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            handleRemoveMember(member.user_id, member.profile?.email || '')
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}

              {members.filter(member => member.user_id !== projectOwnerId).length === 0 && (
                <TableRow>
                  <TableCell colSpan={canManageTeam ? 4 : 3} className="text-center py-8 text-muted-foreground">
                    No team members yet. Invite someone to collaborate!
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      )}

      {/* Pending Invitations Section (Admin/Owner Only) */}
      {canManageTeam && (
        <Card>
          <CardHeader>
            <CardTitle>Pending Invitations</CardTitle>
            <CardDescription>
              Manage invitations that haven't been accepted yet
            </CardDescription>
          </CardHeader>
          <CardContent>
            {invitations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No pending invitations
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invitations.map((invitation) => (
                    <TableRow key={invitation.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          {invitation.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge>{RoleLabels[invitation.role]}</Badge>
                      </TableCell>
                      <TableCell>
                        {formatDistanceToNow(new Date(invitation.expires_at), {
                          addSuffix: true,
                        })}
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyInviteLink(invitation.token)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRevokeInvitation(invitation.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Invite Dialog */}
      <InviteMemberDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        projectId={projectId}
        onInviteSent={() => loadData()}
      />

      {/* Profile Edit Dialog */}
      <ProfileEditDialog
        open={profileDialogOpen}
        onOpenChange={setProfileDialogOpen}
        onProfileUpdated={() => loadProfile()}
      />
    </div>
  )
}
